import { useMemo, useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Store, ShoppingBag, Clock, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { useProducer } from "@/hooks/useProducer";
import { useProducerShopProducts } from "@/hooks/useProducerShopProducts";
import { useProductCoverImages } from "@/hooks/useProductCoverImages";
import { useCoverAsset } from "@/hooks/useCoverAsset";
import { useGalleryAssets } from "@/hooks/useGalleryAssets";
import { ProducerProductCard } from "@/components/farmshop/ProducerProductCard";
import { Seo } from "@/components/common";
import { FarmlyMarkdown } from "@/components/content/FarmlyMarkdown";
import { HeroCarousel, type HeroImage } from "@/components/place/HeroCarousel";
import { track } from "@/lib/analytics";
import { evaluateShopGate, isVisibleInPreview } from "@/lib/shop/shopGate";
import { hasPublicFarmShopAccess } from '@/lib/shop/publicFarmShopAccess';
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useTranslate } from "@/i18n";

import { cn } from "@/lib/utils";

/**
 * ShopGalleryStrip — thumbnail strip matching ProducerGallery/PlaceCoreGalleryStrip pattern.
 * Syncs with hero carousel on mobile, opens lightbox on desktop.
 */
function ShopGalleryStrip({
  thumbUrls,
  fullUrls,
  producerName,
  heroImages,
  activeHeroIndex,
  onSelectIndex,
}: {
  thumbUrls: string[];
  fullUrls: string[];
  producerName: string;
  heroImages: HeroImage[];
  activeHeroIndex: number;
  onSelectIndex: (index: number) => void;
}) {
  const isMobile = useIsMobile();
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  if (thumbUrls.length === 0) return null;

  const maxThumbs = 6;
  const totalCount = thumbUrls.length;
  const showOverlay = totalCount > maxThumbs;
  const visibleThumbs = showOverlay ? thumbUrls.slice(0, maxThumbs - 1) : thumbUrls.slice(0, maxThumbs);
  const extraCount = totalCount - (maxThumbs - 1);

  const handleThumbClick = (index: number) => {
    if (isMobile && heroImages.length > 1) {
      // On mobile, jump the hero carousel (index offset: +1 for cover)
      const heroIdx = index + 1; // gallery images start after cover in heroImages
      if (heroIdx < heroImages.length) {
        onSelectIndex(heroIdx);
      }
    } else {
      setLightboxIdx(index);
    }
  };

  const prev = () => setLightboxIdx(i => (i !== null ? (i - 1 + totalCount) % totalCount : 0));
  const next = () => setLightboxIdx(i => (i !== null ? (i + 1) % totalCount : 0));

  // On mobile, derive which thumb is active based on heroIndex
  const mobileActiveThumb = isMobile && heroImages.length > 1 ? activeHeroIndex - 1 : undefined;

  return (
    <>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
        {visibleThumbs.map((url, i) => (
          <button
            key={url}
            onClick={() => handleThumbClick(i)}
            className={cn(
              "shrink-0 rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
              mobileActiveThumb !== undefined && mobileActiveThumb !== i && "opacity-60",
              mobileActiveThumb === i && "ring-2 ring-primary/50",
            )}
          >
            <img
              src={url}
              alt={`${producerName} gårdsbutik bild ${i + 1}`}
              className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
              loading="lazy"
            />
          </button>
        ))}

        {showOverlay && (
          <button
            onClick={() => handleThumbClick(maxThumbs - 1)}
            className={cn(
              "shrink-0 rounded-lg overflow-hidden relative focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
              mobileActiveThumb !== undefined && mobileActiveThumb !== (maxThumbs - 1) && "opacity-60",
              mobileActiveThumb === (maxThumbs - 1) && "ring-2 ring-primary/50",
            )}
          >
            <img
              src={thumbUrls[maxThumbs - 1]}
              alt={`${producerName} gårdsbutik bild ${maxThumbs}`}
              className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white text-sm font-medium">+{extraCount} bilder</span>
            </div>
          </button>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxIdx !== null} onOpenChange={() => setLightboxIdx(null)}>
        <DialogContent className="max-w-3xl p-0 bg-black border-none">
          {lightboxIdx !== null && (
            <div className="relative flex items-center justify-center min-h-[50vh]">
              <img
                src={fullUrls[lightboxIdx] || thumbUrls[lightboxIdx]}
                alt={`${producerName} gårdsbutik bild ${lightboxIdx + 1}`}
                className="max-h-[80vh] w-auto max-w-full object-contain"
              />
              {totalCount > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"
                    aria-label="Föregående bild"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"
                    aria-label="Nästa bild"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/70 text-xs">
                {lightboxIdx + 1} / {totalCount}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const ProducerShop = () => {
  const t = useTranslate();
  const { slugOrId } = useParams<{ slugOrId: string }>();
  const { producer, enabledFeatures, loading: producerLoading, error: producerError } = useProducer(slugOrId || "");

  const { products, loading: productsLoading, error: productsError } = useProducerShopProducts(
    producer?.id,
    { onlyActive: true }
  );

  const productIds = products.map(p => p.id);
  const { data: coverImages = {} } = useProductCoverImages(productIds, 640);

  // Shop media (owner_type = 'producer_shop')
  const { url640: shopCoverUrl, url320: shopCoverUrl320, asset: shopCoverAsset } = useCoverAsset('producer_shop', producer?.id);
  const { urls640: shopGalleryUrls640, urls320: shopGalleryUrls320, assets: shopGalleryAssets } = useGalleryAssets('producer_shop', producer?.id);

  // Fallback to producer cover if no shop-specific cover
  const { url640: producerCoverUrl } = useCoverAsset('producer', producer?.id);
  const effectiveCoverUrl = shopCoverUrl || producerCoverUrl;
  const hasCover = Boolean(effectiveCoverUrl);

  // Hero index for mobile carousel ↔ gallery sync
  const [heroIndex, setHeroIndex] = useState(0);
  const handleHeroIndexChange = useCallback((index: number) => {
    setHeroIndex(index);
  }, []);

  // Build hero images for carousel: shop cover + shop gallery
  const heroImages: HeroImage[] = useMemo(() => {
    const imgs: HeroImage[] = [];
    const seen = new Set<string>();
    if (shopCoverAsset && shopCoverUrl) {
      seen.add(shopCoverAsset.id);
      imgs.push({ id: shopCoverAsset.id, src: shopCoverUrl, alt: `Gårdsbutik – ${producer?.name ?? ''}` });
    } else if (producerCoverUrl && producer) {
      // Use producer cover as single hero when no shop cover
      imgs.push({ id: 'producer-cover', src: producerCoverUrl, alt: producer.name });
    }
    for (let i = 0; i < shopGalleryAssets.length; i++) {
      const a = shopGalleryAssets[i];
      const url = shopGalleryUrls640[i];
      if (a && url && !seen.has(a.id)) {
        seen.add(a.id);
        imgs.push({ id: a.id, src: url, alt: a.alt_text ?? `Gårdsbutik bild ${i + 1}` });
      }
    }
    return imgs;
  }, [shopCoverAsset, shopCoverUrl, producerCoverUrl, producer, shopGalleryAssets, shopGalleryUrls640]);

  // Gallery thumbnails (only shop gallery, not cover)
  const galleryThumbs = shopGalleryUrls320.length > 0 ? shopGalleryUrls320 : [];
  const galleryFull = shopGalleryUrls640.length > 0 ? shopGalleryUrls640 : [];
  const hasGallery = galleryThumbs.length > 0;

  // Description expand/collapse
  const [descExpanded, setDescExpanded] = useState(false);
  const shopIntro = (producer as any)?.farm_shop_intro as string | undefined;
  const introIsLong = shopIntro ? shopIntro.length > 280 : false;

  const isNotPublished = useMemo(() => {
    return !producer || producer.status !== "public";
  }, [producer]);

  const canonicalUrl = useMemo(() => {
    const baseUrl = window.location.origin;
    if (!producer) return `${baseUrl}/producer/${slugOrId ?? ""}/shop`;
    const path = producer.slug
      ? `/producer/${producer.slug}/shop`
      : `/producer/${producer.id}/shop`;
    return `${baseUrl}${path}`;
  }, [producer, slugOrId]);

  const seoDescription = useMemo(() => {
    if (!producer) return "Lokala produkter direkt från gården.";
    const area = producer.city || producer.county || '';
    const areaText = area ? ` i ${area}` : '';
    return `Gårdsbutik hos ${producer.name}${areaText}. ${producer.short_description || 'Upptäck lokala produkter direkt från gården.'}`.slice(0, 160);
  }, [producer]);

  const hasFarmShop = hasPublicFarmShopAccess({
    enabledFeatures,
    producerHasFarmShop: producer?.has_farm_shop ?? false,
  });
  const isLoading = producerLoading || productsLoading;

  const gate = useMemo(() => {
    if (!producer) return null;
    return evaluateShopGate({
      status: producer.status,
      producer_status: producer.producer_status,
      verified: producer.verified,
      shop_enabled: (producer as any).shop_enabled ?? false,
      hasFarmShopEntitlement: hasFarmShop,
    });
  }, [producer, hasFarmShop]);

  useEffect(() => {
    if (producer && gate?.allowed) {
      track('shop_opened', { producer_id: producer.id, surface: 'shop_page' });
    }
  }, [producer?.id, gate?.allowed]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <main className="flex-1">
          <Skeleton className="w-full aspect-[16/9] md:aspect-[2.4/1]" />
          <div className="container max-w-5xl py-8">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-5 w-48 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-square rounded-lg" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // 404
  if (!producer) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Seo title="Butiken hittades inte" noIndex />
        <SiteHeader />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-3xl mb-4 text-foreground">Vi kunde inte hitta butiken</h1>
          <p className="text-muted-foreground mb-8">
            {producerError || "Butiken du letade efter verkar inte finnas."}
          </p>
          <Button asChild>
            <Link to="/explore">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till Utforska
            </Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // Shop gated
  // Block only for hard gates (needs_public, paused, banned, needs_entitlement).
  // needs_enable / needs_verification are soft: show the shop if products exist.
  if (gate && !gate.allowed && !isVisibleInPreview(gate.reason_key)) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Seo title={`Butik – ${producer.name}`} noIndex />
        <SiteHeader />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-3xl mb-4 text-foreground">{gate.title_sv}</h1>
          <p className="text-muted-foreground mb-8">{gate.message_sv || 'Denna gårdsbutik är inte tillgänglig just nu.'}</p>
          <Button asChild variant="outline">
            <Link to={`/producer/${slugOrId}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('shop.cta.backToProducer')}
            </Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const producerUrl = `/producer/${producer.slug || producer.id}`;

  // Product sections grouped by availability
  const available = products.filter(p => p.status === 'public');
  const upcoming = products.filter(p => p.status === 'upcoming');
  const soldOut = products.filter(p => p.status === 'sold_out');
  const sections = [
    { key: 'available', title: t('shop.section.available'), items: available },
    { key: 'upcoming', title: t('status.comingSoon'), items: upcoming },
    { key: 'sold_out', title: t('status.soldOut'), items: soldOut },
  ].filter(s => s.items.length > 0);
  const showHeadings = sections.length > 1;

  const farmShopStatus = (producer as any).farm_shop_status || 'closed';
  const openingText = (producer as any).farm_shop_opening_hours_text;
  const statusLabels: Record<string, string> = {
    open: t('shop.status.openNow'),
    closed: t('shop.status.closedNow'),
    seasonal: t('status.seasonalOpen'),
  };
  const statusLabel = statusLabels[farmShopStatus] || statusLabels.closed;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title={`Gårdsbutik hos ${producer.name}`}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        ogType="website"
        image={effectiveCoverUrl ?? undefined}
        imageAlt={`Gårdsbutik – ${producer.name}`}
        noIndex={isNotPublished}
      />

      <SiteHeader />

      <main className="flex-1" data-testid="producer-shop-page">
        {/* ── Hero ── */}
        <header className="relative">
          {hasCover ? (
            <div className="relative aspect-[4/3] sm:aspect-[16/9] md:aspect-[2.4/1] w-full overflow-hidden">
              {heroImages.length > 1 ? (
                <HeroCarousel
                  images={heroImages}
                  className="absolute inset-0 w-full h-full"
                  activeIndex={heroIndex}
                  onIndexChange={handleHeroIndexChange}
                />
              ) : (
                <img
                  src={effectiveCoverUrl!}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10 pointer-events-none" />
            </div>
          ) : (
            <div
              className="relative h-48 sm:h-56 md:h-72 overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, hsl(var(--secondary)) 0%, hsl(var(--muted)) 60%, hsl(var(--accent)) 100%)",
              }}
            />
          )}

          {/* Overlay identity */}
          <div className={hasCover ? "absolute bottom-0 left-0 right-0 pb-6 md:pb-10 pt-20 pointer-events-none" : "pb-0"}>
            <div className="container max-w-5xl">
              <div className="max-w-2xl">
                {/* Back link */}
                <Link
                  to={producerUrl}
                  className={`inline-flex items-center gap-1.5 text-sm mb-3 pointer-events-auto transition-colors ${
                    hasCover ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-foreground pt-6"
                  }`}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {producer.name}
                </Link>

                {/* Section label */}
                <div className="flex items-center gap-2.5 mb-1.5">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                    hasCover ? "bg-white/15 backdrop-blur-sm" : "bg-primary/10"
                  }`}>
                    <Store className={`h-4 w-4 ${hasCover ? "text-white" : "text-primary"}`} />
                  </div>
                  <h1
                    className={`font-display text-2xl sm:text-3xl lg:text-4xl font-medium leading-tight ${
                      hasCover ? "text-white" : "text-foreground"
                    }`}
                  >
                    Gårdsbutiken
                  </h1>
                </div>

                <p className={`text-sm sm:text-base ${
                  hasCover ? "text-white/75" : "text-muted-foreground"
                }`}>
                  Lokala produkter från {producer.name}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* ── Gallery strip directly below hero (matches Place/Producer rhythm) ── */}
        {hasGallery && (
          <div className="container max-w-5xl pt-4 pb-2">
            <ShopGalleryStrip
              thumbUrls={galleryThumbs}
              fullUrls={galleryFull}
              producerName={producer.name}
              heroImages={heroImages}
              activeHeroIndex={heroIndex}
              onSelectIndex={handleHeroIndexChange}
            />
          </div>
        )}

        {/* ── Content ── */}
        <div className="container max-w-5xl">

          {/* ── Intro + Status row ── */}
          <div className="py-8 md:py-10 space-y-6">
            {/* Intro text */}
            {shopIntro && (
              <div className="max-w-2xl">
                {introIsLong && !descExpanded ? (
                  <>
                    <div className="line-clamp-4">
                      <FarmlyMarkdown content={shopIntro} variant="body" />
                    </div>
                    <button
                      onClick={() => setDescExpanded(true)}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 mt-2 transition-colors"
                    >
                      Läs mer
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : introIsLong && descExpanded ? (
                  <>
                    <FarmlyMarkdown content={shopIntro} variant="body" />
                    <button
                      onClick={() => setDescExpanded(false)}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 mt-2 transition-colors"
                    >
                      Visa mindre
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : (
                  <FarmlyMarkdown content={shopIntro} variant="body" />
                )}
              </div>
            )}

            {/* Status / opening hours */}
            <div className="flex items-start gap-2.5 text-sm text-muted-foreground p-3.5 rounded-lg bg-muted/40 border border-border/40 max-w-md">
              <Clock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground/70" />
              <div>
                <span className="font-medium text-foreground/80">{statusLabel}</span>
                {openingText && (
                  <p className="mt-0.5 text-muted-foreground/80">{openingText}</p>
                )}
                <p className="mt-1 text-muted-foreground/60 text-xs">
                  {t('shop.intro.preorderPickup')}
                </p>
              </div>
            </div>
          </div>



          {/* ── Products ── */}
          {productsError && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center mb-8">
              <p className="text-destructive mb-4">{productsError}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                {t('cta.tryAgain')}
              </Button>
            </div>
          )}

          {!productsError && products.length > 0 && (
            <section className="pb-12 md:pb-16">
              <h2 className="font-display text-xl sm:text-2xl text-foreground mb-6">
                Finns att ta med hem
              </h2>

              <div className="space-y-10">
                {sections.map(section => (
                  <div key={section.key}>
                    {showHeadings && (
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
                        {section.title}
                      </h3>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                      {section.items.map((product) => (
                        <ProducerProductCard
                          key={product.id}
                          product={{
                            ...product,
                            imageUrl: coverImages[product.id] || product.imageUrl,
                          }}
                          producerId={producer.id}
                          producerName={producer.name}
                          producerSlug={producer.slug}
                          pickupInstructions={producer.pickup_instructions}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!productsError && products.length === 0 && (
            <section className="pb-12 md:pb-16">
              <div className="rounded-xl border border-border bg-card p-12 text-center">
                <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="h-7 w-7 text-muted-foreground" />
                </div>
                <h2 className="font-display text-xl text-foreground mb-2">{t('shop.empty.title')}</h2>
                <p className="text-muted-foreground max-w-md mx-auto text-sm">
                  {t('shop.empty.body')}
                </p>
              </div>
            </section>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ProducerShop;
